import React from "react";
import COLORS from "../../constants/colors";
import FlowTopBar from "../common/FlowTopBar";
import HeartView from "../common/HeartView";
import {
  btnBarStyle,
  centerSubStyle,
  exampleBoxStyle,
  heroWordStyle,
  hintBtnStyle,
  knowBtnStyle,
  mainCardStyle,
  pageStyle,
} from "../../styles/commonStyles";
import { speakFrench } from "../../utils/speech";

function highlightWord(example, word) {
  if (!example || !word) return example || "";
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return example.replace(new RegExp(escaped, "gi"), `<strong>${word}</strong>`);
}

function getWordImageBlock(word) {
  if (word?.image) {
    return (
      <img
        src={word.image}
        alt={word.word}
        style={{
          width: "100%",
          height: "180px",
          objectFit: "cover",
          borderRadius: "16px",
          marginBottom: "1rem",
          border: "1px solid #EAECEF",
        }}
      />
    );
  }

  // 如果没有图片，返回空白渐变框，不显示文字
  return (
    <div
      style={{
        width: "100%",
        height: "180px",
        borderRadius: "16px",
        marginBottom: "1rem",
        border: "1px solid #EAECEF",
        background: "linear-gradient(135deg, #EEF4FB 0%, #FFF7EE 100%)",
      }}
    />
  );
}

// 拆分提示状态（0=未提示，1=法语提示，2=汉语提示）
export default function LearnPhaseTwo({
  word,
  progressText,
  heartCount,
  showHint,
  onShowHint,
  onKnow,
  onDontKnow,
  onPrev,
  onStar,
  onSkip,
  onHome,
  disablePrev = false,
}) {
  // 内部管理提示阶段：0-未提示，1-已显示法语提示，2-已显示汉语提示
  const [hintStage, setHintStage] = React.useState(0);

  // 如果外部已经标记过提示，则保持至少显示法语提示；换词时重置
  React.useEffect(() => {
    if (showHint) {
      setHintStage((prev) => Math.max(prev, 1));
    } else {
      setHintStage(0);
    }
  }, [showHint, word?.id]);
  
  // 处理提示点击逻辑
  const handleHintClick = () => {
    if (hintStage === 0) {
      // 第一次点击：显示法语提示
      setHintStage(1);
    } else if (hintStage === 1) {
      // 第二次点击：显示汉语提示
      setHintStage(2);
    }
    // 保留原有回调（如果外部有统计等逻辑）
    if (onShowHint) onShowHint();
  };

  if (!word) return null;

  return (
    <div style={pageStyle()}>
      <div style={mainCardStyle()}>
        {/* 顶部导航 */}
        <FlowTopBar
          title={`学习进度：${progressText}`}
          onPrev={onPrev}
          onStar={onStar}
          onSkip={onSkip}
          onHome={onHome}
          disablePrev={disablePrev}
        />

        {/* 爱心 */}
        <div style={{ display: "flex", justifyContent: "center", margin: "0.5rem 0" }}>
          <HeartView count={heartCount} />
        </div>

        {/* 单词 + 发音 */}
        <div
          style={{ textAlign: "center", cursor: "pointer", marginBottom: "0.5rem" }}
          onClick={() => speakFrench(word.word)}
        >
          <div style={heroWordStyle()}>{word.word}</div>
          <div style={{ ...centerSubStyle(), marginTop: "0.2rem", color: COLORS.subtext }}>
            {word.phonetic} 🔊
          </div>
        </div>

        {/* 图片或空白框 */}
        <div>{getWordImageBlock(word)}</div>

        {/* ========== 已删除红色圈住的空白大面板 ========== */}
        
        {/* 法语提示面板（第一次点击显示） */}
        <div
          style={{
            width: "100%",
            height: "60px",
            borderRadius: "12px",
            border: "1px solid #EAECEF",
            marginBottom: "0.8rem",
            backgroundColor: "#F9F9F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 1rem",
          }}
        >
          {hintStage >= 1 && (
            <span style={{ color: COLORS.text, fontSize: "1rem", fontWeight: 500 }}>
              {word.frenchHint || `Définition: ${word.word}`}
            </span>
          )}
        </div>
        
        {/* 汉语提示面板（第二次点击显示） */}
        <div
          style={{
            width: "100%",
            height: "60px",
            borderRadius: "12px",
            border: "1px solid #EAECEF",
            marginBottom: "1rem",
            backgroundColor: "#F9F9F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 1rem",
          }}
        >
          {hintStage >= 2 && (
            <span style={{ color: COLORS.blue, fontSize: "1rem", fontWeight: 600 }}>
              {word.translation || "暂无中文释义"}
            </span>
          )}
        </div>

        {/* 例句 */}
        <div style={exampleBoxStyle()}>
          <span
            dangerouslySetInnerHTML={{
              __html: highlightWord(word.example, word.word),
            }}
          />
          {/* 例句翻译跟随汉语提示一起显示 */}
          {hintStage >= 2 && (
            <div style={{ marginTop: "0.7rem", color: COLORS.subtext }}>
              {word.exampleTranslation}
            </div>
          )}
        </div>

        {/* 提示按钮（根据阶段修改文案） */}
        <div style={{ textAlign: "center", margin: "1rem 0" }}>
          <button onClick={handleHintClick} style={hintBtnStyle()}>
            💡
            <div style={{ fontSize: "0.86rem", marginTop: "0.2rem" }}>
              {hintStage === 0 ? "提示一下" : hintStage === 1 ? "显示中文意思" : "已显示全部提示"}
            </div>
          </button>
        </div>

        {/* 操作按钮 */}
        <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
          <button onClick={onKnow} style={knowBtnStyle()}>
            认识
            <div style={btnBarStyle(COLORS.green)} />
          </button>
          <button onClick={onDontKnow} style={knowBtnStyle()}>
            不认识
            <div style={btnBarStyle(COLORS.red)} />
          </button>
        </div>
      </div>
    </div>
  );
}